import {
  Box,
  Card,
  Checkbox,
  Grid,
  H3,
  H4,
  Input,
  Stack,
  TextArea
} from "@adfenix/alexandria";
import { useFormContext } from "react-hook-form";
import Divider from "components/Divider";
import { Modal } from "components/Modal";
import { Campaign } from "../campaign/Campaign";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "../useDisabledFields";
import { numberValidator } from "utils/react-hook-form";
import { PaymentSettings } from "./PaymentSettings";
import { Dispatch, SetStateAction } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from ".";

interface Props {
  bundleGroupIndex: number;
  bundleIndex: number;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  onCancel: () => void;
  isNew: boolean;
}

const StyledModalCard = styled(Modal.Card)`
  padding: 32px !important;
`;

const StyledCloseIcon = styled.i`
  font-size: 24px;
  cursor: pointer;
  margin-right: -5px;
`;

export const CreateOrEditBundle = ({
  bundleGroupIndex,
  setShowModal,
  onCancel,
  bundleIndex,
  isNew
}: Props) => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { register } = useFormContext<ProgramService.ProgramModel>();

  return (
    <StyledModalCard $bigCard={true}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <H3>
          {isNew
            ? t("programBuilder.program.details.bundle.header.add")
            : t("programBuilder.program.details.bundle.header.edit")}
        </H3>
        <StyledCloseIcon className="uil uil-times" onClick={() => onCancel()} />
      </Box>

      <Modal.Card.Body style={{ overflow: "visible" }}>
        <Box pY="m">
          <Card>
            <Stack spacing="l">
              <H4>{t("programBuilder.program.details.bundle.header.label")}</H4>

              <Grid
                gap="l"
                columns={["1fr", "1fr 1fr"]}
                style={{ maxWidth: formMaxWidth }}
              >
                <Input
                  label={t(
                    "programBuilder.program.details.bundle.input.name.label"
                  )}
                  {...register(
                    `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.name`,
                    {
                      required: t(
                        "programBuilder.program.details.bundle.input.name.required.message"
                      )
                    }
                  )}
                  readOnly={disabledFields("bundleGroups.bundles.name")}
                />
                <div style={{ gridColumn: "span 2" }}>
                  <Checkbox
                    label={t(
                      "programBuilder.program.details.bundle.checkbox.default"
                    )}
                    {...register(
                      `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.default`
                    )}
                    readOnly={disabledFields("bundleGroups.bundles.default")}
                  />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <TextArea
                    style={{ maxWidth: "100%" }}
                    {...register(
                      `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.description`
                    )}
                    label={t(
                      "programBuilder.program.details.bundle.input.description.label"
                    )}
                    readOnly={disabledFields(
                      "bundleGroups.bundles.description"
                    )}
                  />
                </div>

                <Input
                  {...register(
                    `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.estimatedReach`,
                    {
                      valueAsNumber: true,
                      validate: numberValidator(
                        t(
                          "programBuilder.program.details.bundle.input.estimatedReach.validation.message"
                        )
                      )
                    }
                  )}
                  label={t(
                    "programBuilder.program.details.bundle.input.estimatedReach.label"
                  )}
                  readOnly={disabledFields(
                    "bundleGroups.bundles.estimatedReach"
                  )}
                />
                <Input
                  {...register(
                    `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.maxNumberOfOrders`,
                    {
                      required: t(
                        "programBuilder.program.details.bundle.input.maxOrders.required.message"
                      )
                    }
                  )}
                  label={t(
                    "programBuilder.program.details.bundle.input.maxOrders.label"
                  )}
                  readOnly={disabledFields(
                    "bundleGroups.bundles.maxNumberOfOrders"
                  )}
                />
              </Grid>
            </Stack>

            <Divider />

            <PaymentSettings
              bundleGroupIndex={bundleGroupIndex}
              bundleIndex={bundleIndex}
            />
          </Card>
        </Box>

        <Stack spacing="m">
          <Campaign
            bundleGroupIndex={bundleGroupIndex}
            bundleIndex={bundleIndex}
            setShowModal={setShowModal}
            onCancel={onCancel}
            isNew={isNew}
          />
        </Stack>
      </Modal.Card.Body>
    </StyledModalCard>
  );
};
